import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Sparkles, Archive } from "lucide-react";
import Reveal from "./Reveal";
import { GAMES_FALLBACK } from "../mock/mock";
import { Star, Wave } from "./Decor";
import ComingSoonSignup from "./ComingSoonSignup";
import { TrailerHoverPreview } from "./TrailerHoverPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Projects() {
  const [games, setGames] = useState(GAMES_FALLBACK);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/games`, { timeout: 25000 })
      .then((res) => {
        if (active && Array.isArray(res.data) && res.data.length > 0) setGames(res.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const featured = games.filter((g) => g.category === "featured");
  const soon = games.filter((g) => g.category === "coming-soon");
  const archived = games.filter((g) => g.category === "archived");

  return (
    <section id="works" className="relative bg-slate-900 py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[120px]" />
      <Star className="absolute top-24 right-[8%] w-12 h-12 text-orange-400/50 animate-deco" />
      <Wave className="absolute bottom-16 left-[6%] w-28 text-cyan-400/40 animate-deco" />

      <div className="relative max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="text-cyan-400 font-bold tracking-widest uppercase text-sm mb-3">Selected Works</p>
          <h2 className="font-display font-800 text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-tight">
            Games we've poured our <span className="text-gradient">heart</span> into.
          </h2>
        </Reveal>

        {/* Featured */}
        <div className="mt-16 space-y-8">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <Link
                to={`/game/${p.id}`}
                className="tilt-card group grid md:grid-cols-2 gap-8 items-center rounded-[2rem] overflow-hidden bg-white/5 border-2 border-white/10 hover:border-white/30 transition-colors p-4 sm:p-6"
                style={{ boxShadow: `8px 8px 0 ${p.accent}66` }}
              >
                <div className={`relative rounded-2xl overflow-hidden aspect-[16/9] bg-slate-800 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <TrailerHoverPreview image={p.image} alt={p.title} hls={p.trailer && p.trailer.hls} youtubeId={p.trailer && p.trailer.youtubeId} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent pointer-events-none" />
                </div>
                <div className={`px-2 sm:px-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ background: `${p.accent}22`, color: p.accent }}>
                      {p.genre}
                    </span>
                    <span className="text-white/40 text-sm font-medium">{p.year}</span>
                    {p.price && p.price.discount > 0 && (
                      <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-green-500/20 text-green-300">
                        <span>-{p.price.discount}%</span>
                        {p.price.initial && <span className="line-through opacity-60">{p.price.initial}</span>}
                        <span>{p.price.final}</span>
                      </span>
                    )}
                    {p.price && p.price.discount === 0 && p.price.final && (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-white/10 text-white/80">{p.price.final}</span>
                    )}
                    {p.hasDemo && (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-cyan-500/20 text-cyan-300">Demo available</span>
                    )}
                  </div>
                  <h3 className="font-display font-800 text-3xl sm:text-4xl text-white mb-4 flex items-center gap-3">
                    {p.title}
                    <ArrowUpRight className="w-7 h-7 text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </h3>
                  <p className="text-white/60 text-lg leading-relaxed">{p.description}</p>
                  <span className="inline-block mt-6 font-semibold text-cyan-400 group-hover:text-cyan-300">View details →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Coming soon */}
        {soon.length > 0 && (
          <div className="mt-24">
            <Reveal>
              <h3 className="flex items-center gap-2 font-display font-700 text-2xl text-white mb-8">
                <Sparkles className="w-6 h-6 text-cyan-400" /> Coming Soon
              </h3>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-6">
              {soon.map((g, i) => (
                <Reveal key={g.id} delay={i * 100}>
                  <div className="relative overflow-hidden rounded-[1.75rem] p-8 border-2 border-dashed border-white/20 bg-gradient-to-br from-white/5 to-transparent">
                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-2xl" style={{ background: `${g.accent}33` }} />
                    <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" style={{ background: `${g.accent}22`, color: g.accent }}>
                      In Development
                    </span>
                    <h4 className="font-display font-800 text-3xl text-white">{g.title}</h4>
                    <p className="text-white/50 mt-2">Something new is brewing in the bowl. Drop your email and we'll ping you when it launches!</p>
                    <ComingSoonSignup gameId={g.id} gameTitle={g.title} />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {/* Archived */}
        {archived.length > 0 && (
          <div className="mt-24">
            <Reveal>
              <h3 className="flex items-center gap-2 font-display font-700 text-2xl text-white mb-8">
                <Archive className="w-6 h-6 text-white/50" /> From the Archives
              </h3>
            </Reveal>
            <div className="grid gap-6">
              {archived.map((g, i) => (
                <Reveal key={g.id} delay={i * 100}>
                  <Link
                    to={`/game/${g.id}`}
                    className="group flex flex-col sm:flex-row gap-6 items-center rounded-[1.75rem] overflow-hidden bg-white/5 border border-white/10 hover:border-white/25 transition-colors p-5"
                  >
                    <div className="w-full sm:w-48 shrink-0 aspect-video sm:aspect-square rounded-xl overflow-hidden bg-slate-800">
                      <img src={g.image} alt={g.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-white/10 text-white/60">Archived · {g.year}</span>
                      </div>
                      <h4 className="font-display font-800 text-2xl text-white flex items-center gap-2">
                        {g.title}
                        <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </h4>
                      <p className="text-white/50 mt-2 max-w-xl">{g.description}</p>
                      <span className="inline-block mt-3 text-sm font-semibold text-cyan-400 group-hover:text-cyan-300">View game →</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
